import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import JourneyCompletionSuccess from '@cohort/wallet/components/modals/journey/JourneyCompletionSuccess';
import JourneyOptInSuccess from '@cohort/wallet/components/modals/journey/JourneyOptInSuccess';
import {campaignsKeys} from '@cohort/wallet/hooks/api/Campaign';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {spacesKeys} from '@cohort/wallet/hooks/api/Space';
import {useApi} from '@cohort/wallet/hooks/useApi';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {JourneyParticipationsApi} from '@cohort/wallet/lib/Endpoints';
import {getDigitalAssetsListRoute} from '@cohort/wallet/lib/Pages';
import type {CampaignJourneyWDto} from '@cohort/wallet-schemas/campaign';
import type {JourneyParticipationWDto} from '@cohort/wallet-schemas/journeyParticipation';
import {Gift} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import JSConfetti from 'js-confetti';
import {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

type JourneyActionButtonProps = {
  campaign: CampaignJourneyWDto;
};

type OptInBtnProps = JourneyActionButtonProps & {
  onOptInSuccess: () => void;
};

type CompleteJourneyBtnProps = {
  campaign: CampaignJourneyWDto;
  participation: NonNullable<CampaignJourneyWDto['journey']['participation']>;
  onJourneyCompletedSuccess: (participation: JourneyParticipationWDto) => void;
};

const OptInBtn: React.FC<OptInBtnProps> = ({campaign, onOptInSuccess}) => {
  const joinCampaign = useApi(JourneyParticipationsApi.joinJourneyCampaign);

  const {mutate, isLoading} = useCohortMutation({
    mutationFn: async () => joinCampaign(campaign.id),
    onSuccess: onOptInSuccess,
  });

  return (
    <PrimaryButton
      className="w-full"
      text={campaign.journey.optInCta}
      type="button"
      tracking={{
        namespace: 'journeys.detail.optIn',
        metadata: {
          journeyId: campaign.id,
        },
      }}
      isLoading={isLoading}
      onClick={() => mutate()}
    />
  );
};

const CompleteJourneyButton: React.FC<CompleteJourneyBtnProps> = ({
  campaign,
  participation,
  onJourneyCompletedSuccess,
}) => {
  const {hasDarkBg} = useThemeContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.journeyActionButton'});
  const completeJourney = useApi(JourneyParticipationsApi.completeJourney);

  const {mutate, isLoading} = useCohortMutation({
    mutationFn: async () => completeJourney(participation.id),
    onSuccess: onJourneyCompletedSuccess,
  });

  if (participation.canBeCompleted && campaign.status !== 'ended') {
    return (
      <PrimaryButton
        className="w-full"
        text={
          <Fragment>
            <Gift className="mr-2 h-5 w-5" />
            {participation.eligibleForReward ? t('claimReward') : t('complete')}
          </Fragment>
        }
        type="button"
        tracking={{
          namespace: 'journeys.detail.claimReward',
          metadata: {
            journeyId: campaign.id,
          },
        }}
        isLoading={isLoading}
        onClick={() => {
          mutate();
        }}
      />
    );
  }

  return (
    <div
      className={classNames(
        'flex cursor-not-allowed items-center justify-center rounded-lg px-4 py-3 text-sm font-medium',
        hasDarkBg ? 'bg-white/30 text-black/80' : 'bg-black/4 text-black/40'
      )}
    >
      <Gift className="mr-2 h-5 w-5" />
      {participation.isCompleted &&
        (participation.rewardClaimed ? t('rewardAlreadyClaimed') : t('completed'))}
      {!participation.isCompleted &&
        (participation.eligibleForReward ? t('claimReward') : t('complete'))}
    </div>
  );
};

const JourneyActionButton: React.FC<JourneyActionButtonProps> = ({campaign}) => {
  const merchant = useMerchantContext();
  const queryClient = useQueryClient();
  const [registerJourneyModal, setRegisterJourneyModal] = useState(false);
  const [completionModal, setCompletionModal] = useState(false);
  const [participation, setParticipation] = useState(campaign.journey.participation);
  const {setTransitioning} = useSpaceContext();
  const navigate = useNavigate();
  const confetti = useRef(new JSConfetti());

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => confetti.current.clearCanvas();
  }, []);

  const refreshCampaignAndSpace = useCallback(() => {
    queryClient.invalidateQueries(campaignsKeys.getJourneyById(merchant.id, campaign.id));
    queryClient.invalidateQueries(spacesKeys.getSpace());
  }, [campaign.id, merchant, queryClient]);

  const handleJourneyCompleted = (participation: JourneyParticipationWDto): void => {
    confetti.current.addConfetti({
      confettiColors: ['#FFD700', '#FF69B4', '#00CED1', '#FF6347', '#7CFC00', '#9932CC'],
    });
    setParticipation(participation);
    setCompletionModal(true);
    refreshCampaignAndSpace();
  };

  return (
    <Fragment>
      {campaign.journey.participation === null ? (
        <OptInBtn
          campaign={campaign}
          onOptInSuccess={() => {
            setRegisterJourneyModal(true);
            refreshCampaignAndSpace();
          }}
        />
      ) : (
        <CompleteJourneyButton
          campaign={campaign}
          participation={campaign.journey.participation}
          onJourneyCompletedSuccess={handleJourneyCompleted}
        />
      )}
      {registerJourneyModal && (
        <JourneyOptInSuccess
          campaignId={campaign.id}
          onClose={() => setRegisterJourneyModal(false)}
        />
      )}
      {completionModal && participation?.isCompleted && (
        <JourneyCompletionSuccess
          campaign={campaign}
          participation={participation}
          onClose={() => {
            setCompletionModal(false);
            setTransitioning({
              active: true,
              enterFrom: 'left',
            });
            navigate(getDigitalAssetsListRoute());
          }}
        />
      )}
    </Fragment>
  );
};

export default JourneyActionButton;
